import utils from "@bigcommerce/stencil-utils";
import ProductDetailsBase, {
  optionChangeDecorator,
} from "./product-details-base";
import "foundation-sites/js/foundation/foundation";
import "foundation-sites/js/foundation/foundation.reveal";
import ImageGallery from "../product/image-gallery";
import modalFactory, { alertModal, showAlertModal } from "../global/modal";
import { isEmpty, isPlainObject } from "lodash";
import nod from "../common/nod";
import { announceInputErrorMessage } from "../common/utils/form-utils";
import { retry } from "../common/utils/misc";
import forms from "../common/models/forms";
import { normalizeFormData } from "./utils/api";
import { isBrowserIE, convertIntoArray } from "./utils/ie-helpers";
import bannerUtils from "./utils/banner-utils";
import currencySelector from "../global/currency-selector";
import {
  addProductToCartViaApi as addProductToCartViaApiService,
  getCart,
  removeInvalidItemsFromCart,
  InvalidInputFaceValueError,
} from "../../../../services/cart";
import { getCurrencySymbol } from "./utils/currency";
import { getCountryNameFromCode } from "../../../../utils/country";
import {
  getProductExpiryDisplayText,
  getProductRedeemableAtDisplayText,
} from "../../../../utils/product";
import {
  replaceTranslationPlaceholders,
  localiseCurrency,
} from "../common/utils/translations-utils";

const INPUT_OPEN_VALUE_PRODUCT_MODIFIER_NAME = "Enter amount";
export default class ProductDetails extends ProductDetailsBase {
  constructor($scope, context, productAttributesData = {}) {
    super($scope, context);

    this.$overlay = $("[data-cart-item-add] .loadingOverlay");
    this.imageGallery = new ImageGallery(
      $("[data-image-gallery]", this.$scope)
    );
    this.imageGallery.init();
    this.listenQuantityChange();
    this.$swatchOptionMessage = $(".swatch-option-message");
    this.swatchInitMessageStorage = {};
    this.swatchGroupIdList = $('[id^="swatchGroup"]').map((_, group) =>
      $(group).attr("id")
    );
    this.storeInitMessagesForSwatches();

    const $form = $("form[data-cart-item-add]", $scope);

    if ($form[0].checkValidity()) {
      this.updateProductDetailsData();
    } else {
      this.toggleWalletButtonsVisibility(false);
    }

    this.addToCartValidator = nod({
      submit: $form.find("input#form-action-addToCart"),
      tap: announceInputErrorMessage,
    });

    const $productOptionsElement = $("[data-product-option-change]", $form);
    const hasOptions = $productOptionsElement.html().trim().length;
    const hasDefaultOptions =
      $productOptionsElement.find("[data-default]").length;
    const $productSwatchGroup = $('[id*="attribute_swatch"]', $form);
    const $productSwatchLabels = $(".form-option-swatch", $form);
    const placeSwatchLabelImage = (_, label) => {
      const $optionImage = $(".form-option-expanded", $(label));
      const optionImageWidth = $optionImage.outerWidth();
      const extendedOptionImageOffsetLeft = 55;
      const { right } = label.getBoundingClientRect();
      const emptySpaceToScreenRightBorder = window.screen.width - right;
      const shiftValue = optionImageWidth - emptySpaceToScreenRightBorder;

      if (
        emptySpaceToScreenRightBorder <
        optionImageWidth + extendedOptionImageOffsetLeft
      ) {
        $optionImage.css(
          "left",
          `${shiftValue > 0 ? -shiftValue : shiftValue}px`
        );
      }
    };

    $(window).on("load", () => {
      this.registerAddToCartValidation();
      $.each($productSwatchLabels, placeSwatchLabelImage);
    });

    if (context.showSwatchNames) {
      this.$swatchOptionMessage.removeClass("u-hidden");

      $productSwatchGroup.on("change", ({ target }) => {
        const swatchGroupElement = target.parentNode.parentNode;

        this.showSwatchNameOnOption($(target), $(swatchGroupElement));
      });

      $.each($productSwatchGroup, (_, element) => {
        const swatchGroupElement = element.parentNode.parentNode;

        if ($(element).is(":checked"))
          this.showSwatchNameOnOption($(element), $(swatchGroupElement));
      });
    }

    $productOptionsElement.on("change", (event) => {
      this.productOptionsChanged(event);
      this.setProductVariant();
    });

    $form.on("submit", (event) => {
      this.addToCartValidator.performCheck();

      if (this.addToCartValidator.areAll("valid")) {
        this.addProductToCart(event, $form[0]);
      }
    });

    // Update product attributes. Also update the initial view in case items are oos
    // or have default variant properties that change the view
    if ((isEmpty(productAttributesData) || hasDefaultOptions) && hasOptions) {
      const $productId = $('[name="product_id"]', $form).val();
      const optionChangeCallback = optionChangeDecorator.call(
        this,
        hasDefaultOptions
      );

      utils.api.productAttributes.optionChange(
        $productId,
        $form.serialize(),
        "products/bulk-discount-rates",
        optionChangeCallback
      );
    } else {
      this.updateProductAttributes(productAttributesData);
      bannerUtils.dispatchProductBannerEvent(productAttributesData);
    }

    $productOptionsElement.show();

    this.previewModal = modalFactory("#previewModal")[0];
    this.removeInternalFaceValueModifierField();
    this.initializeProductValueField();
    this.setRedemptionInformation();

    // Resize buttons based on text length and window screen size
    const expandedScreenQuery = window.matchMedia("(min-width: 768px)");
    this.resizeButtons();
    expandedScreenQuery.addEventListener("change", this.resizeButtons);
  }

  /**
   * Increase the length of the add to cart and checkout buttons,
   * if the rendered localised text overfills the default size of either button.
   * Otherwise, reset the length of add to cart and checkout buttons,
   * if the window size is small enough that text in the buttons will never overfill.
   */
  resizeButtons() {
    const addToCartButton = document.getElementById("form-action-addToCart");
    const checkoutButton = document.getElementById(
      "form-action-addToCartAndCheckout"
    );
    const buttonsContainer = document.querySelector(".add-to-cart-buttons");
    if (!addToCartButton || !checkoutButton || !buttonsContainer) return;

    const addToCartButtonWidth = addToCartButton.getBoundingClientRect().width;
    const checkoutButtonWidth = checkoutButton.getBoundingClientRect().width;

    const addToCartOverflow =
      addToCartButton.scrollWidth >= addToCartButtonWidth;
    const checkoutOverflow = checkoutButton.scrollWidth >= checkoutButtonWidth;
    const containerOverflow =
      addToCartButtonWidth + checkoutButtonWidth >
      buttonsContainer.getBoundingClientRect().width;

    const shouldExpand =
      addToCartOverflow || checkoutOverflow || containerOverflow;
    if (shouldExpand) {
      buttonsContainer.classList.add("add-to-cart-buttons--wide");
    } else {
      buttonsContainer.classList.remove("add-to-cart-buttons--wide");
    }
  }

  /**
   * Remove the internal face value modifier field as its only used internally
   * for storing face value with cart line items.
   */
  removeInternalFaceValueModifierField() {
    const internalFaceValueModifierId = currentProductDetails.options.find(
      (op) => op.display_name === "internal_face_value"
    )?.id;
    if (internalFaceValueModifierId) {
      $(`#attribute_number_${internalFaceValueModifierId}`).parent().remove();
    }
  }
  /**
   * Initialize the product value field for open value products.
   * This method sets the placeholder, min, max attributes and makes the field required.
   * It also checks if the product is an open value product and sets the appropriate placeholder.
   */
  initializeProductValueField() {
    // get product value modifier options
    const openValueFieldOptions = this.getOpenValueFieldOptions();
    if (!openValueFieldOptions) {
      console.log(
        "Open value field modifier options not found. Either the product is not an open value product or the modifier is not configured correctly."
      );

      // translate field label for fixed denominations
      const fixedValueLabelElement = $(
        ".product-variant > div label#rectangle-group-label"
      );
      fixedValueLabelElement?.text(
        window.translations.denomination_field_label
      );
      return;
    }
    // set field to required
    const numberField = $(`#attribute_number_${openValueFieldOptions?.id}`);
    if (numberField) {
      numberField.val(null);
      numberField.removeAttr("value");
      numberField.attr("required", true);
    }

    // set placeholder
    let placeholder = "";
    const currency = currentProductDetails.custom_fields.find(
      (field) => field.name === "currency"
    );

    const productMinValue = openValueFieldOptions.lowest;
    const productMaxValue = openValueFieldOptions.highest;
    if (!isNaN(productMinValue) && !isNaN(productMaxValue)) {
      placeholder += ` ${localiseCurrency(
        productMinValue
      )} - ${localiseCurrency(productMaxValue)}`;
      numberField.attr("min", productMinValue);
      numberField.attr("max", productMaxValue);
    }
    numberField.attr("placeholder", placeholder);
    numberField.attr("data-test", "open-value-input-field");

    // set translated field label
    const openValueLabelElement = $(
      `label[for="attribute_number_${openValueFieldOptions.id}"]`
    );
    openValueLabelElement?.text(window.translations.enter_amount_field_label);
  }
  registerAddToCartValidation() {
    this.addToCartValidator.add([
      {
        selector: "[data-quantity-change] > .form-input--incrementTotal",
        validate: (cb, val) => {
          const result = forms.numbersOnly(val);
          cb(result);
        },
        errorMessage: this.context.productQuantityErrorMessage,
      },
    ]);

    return this.addToCartValidator;
  }

  storeInitMessagesForSwatches() {
    if (
      this.swatchGroupIdList.length &&
      isEmpty(this.swatchInitMessageStorage)
    ) {
      this.swatchGroupIdList.each((_, swatchGroupId) => {
        if (!this.swatchInitMessageStorage[swatchGroupId]) {
          this.swatchInitMessageStorage[swatchGroupId] = $(
            `#${swatchGroupId} ~ .swatch-option-message`
          )
            .text()
            .trim();
        }
      });
    }
  }

  setProductVariant() {
    const unsatisfiedRequiredFields = [];
    const options = [];

    $.each($("[data-product-attribute]"), (index, value) => {
      const optionLabel = value.children[0].innerText;
      const optionTitle = optionLabel.split(":")[0].trim();
      const required = optionLabel.toLowerCase().includes("required");
      const type = value.getAttribute("data-product-attribute");

      if (
        (type === "input-file" ||
          type === "input-text" ||
          type === "input-number") &&
        value.querySelector("input").value === "" &&
        required
      ) {
        unsatisfiedRequiredFields.push(value);
      }

      if (
        type === "textarea" &&
        value.querySelector("textarea").value === "" &&
        required
      ) {
        unsatisfiedRequiredFields.push(value);
      }

      if (type === "date") {
        const isSatisfied = Array.from(value.querySelectorAll("select")).every(
          (select) => select.selectedIndex !== 0
        );

        if (isSatisfied) {
          const dateString = Array.from(value.querySelectorAll("select"))
            .map((x) => x.value)
            .join("-");
          options.push(`${optionTitle}:${dateString}`);

          return;
        }

        if (required) {
          unsatisfiedRequiredFields.push(value);
        }
      }

      if (type === "set-select") {
        const select = value.querySelector("select");
        const selectedIndex = select.selectedIndex;

        if (selectedIndex !== 0) {
          options.push(
            `${optionTitle}:${select.options[selectedIndex].innerText}`
          );

          return;
        }

        if (required) {
          unsatisfiedRequiredFields.push(value);
        }
      }

      if (
        type === "set-rectangle" ||
        type === "set-radio" ||
        type === "swatch" ||
        type === "input-checkbox" ||
        type === "product-list"
      ) {
        const checked = value.querySelector(":checked");
        if (checked) {
          const getSelectedOptionLabel = () => {
            const productVariantslist = convertIntoArray(value.children);
            const matchLabelForCheckedInput = (inpt) =>
              inpt.dataset.productAttributeValue === checked.value;
            return productVariantslist.filter(matchLabelForCheckedInput)[0];
          };
          if (
            type === "set-rectangle" ||
            type === "set-radio" ||
            type === "product-list"
          ) {
            const label = isBrowserIE
              ? getSelectedOptionLabel().innerText.trim()
              : checked.labels[0].innerText;
            if (label) {
              options.push(`${optionTitle}:${label}`);
            }
          }

          if (type === "swatch") {
            const label = isBrowserIE
              ? getSelectedOptionLabel().children[0]
              : checked.labels[0].children[0];
            if (label) {
              options.push(`${optionTitle}:${label.title}`);
            }
          }

          if (type === "input-checkbox") {
            options.push(`${optionTitle}:Yes`);
          }

          return;
        }

        if (type === "input-checkbox") {
          options.push(`${optionTitle}:No`);
        }

        if (required) {
          unsatisfiedRequiredFields.push(value);
        }
      }
    });

    let productVariant =
      unsatisfiedRequiredFields.length === 0
        ? options.sort().join(", ")
        : "unsatisfied";
    const view = $(".productView");
    if (productVariant) {
      productVariant = productVariant === "unsatisfied" ? "" : productVariant;
      if (view.attr("data-event-type")) {
        view.attr("data-product-variant", productVariant);
      } else {
        const productName = view
          .find(".productView-title")[0]
          .innerText.replace(/"/g, "\\$&");
        const card = $(`[data-name="${productName}"]`);
        card.attr("data-product-variant", productVariant);
      }
    }
  }

  /**
   * Checks if the current window is being run inside an iframe
   * @returns {boolean}
   */
  isRunningInIframe() {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  }

  /**
   *
   * Handle product options changes
   *
   */
  productOptionsChanged(event) {
    const $changedOption = $(event.target);
    const $form = $changedOption.parents("form");
    const productId = $('[name="product_id"]', $form).val();

    // Do not trigger an ajax request if it's a file or if the browser doesn't support FormData
    if (
      $changedOption.attr("type") === "file" ||
      window.FormData === undefined
    ) {
      return;
    }

    utils.api.productAttributes.optionChange(
      productId,
      $form.serialize(),
      "products/bulk-discount-rates",
      (err, response) => {
        if (err) {
          console.error(err);
          showAlertModal(window.translations.generic_error_message);
          return;
        }
        const productAttributesData = response.data || {};
        const productAttributesContent = response.content || {};
        this.updateProductAttributes(productAttributesData);
        this.updateView(productAttributesData, productAttributesContent);
        this.updateProductDetailsData();
        bannerUtils.dispatchProductBannerEvent(productAttributesData);

        if (!this.checkIsQuickViewChild($form)) {
          const $context = $form
            .parents(".productView")
            .find(".productView-info");
          modalFactory("[data-reveal]", { $context });
        }
      }
    );
  }

  /**
   * Get product attributes using storefront product attributes API
   * @returns Product attributes object
   */
  getProductAttributes() {
    return new Promise((resolve, reject) => {
      const $form = $("form[data-cart-item-add]");
      const productId = $('[name="product_id"]', $form).val();
      utils.api.productAttributes.optionChange(
        productId,
        $form.serialize(),
        null,
        (err, res) => {
          if (err) {
            reject(err);
          }
          resolve(res);
        }
      );
    });
  }

  /**
   * if this setting is enabled in Page Builder
   * show name for swatch option
   */
  showSwatchNameOnOption($swatch, $swatchGroup) {
    const swatchName = $swatch.attr("aria-label");
    const activeSwatchGroupId = $swatchGroup.attr("aria-labelledby");
    const $swatchOptionMessage = $(
      `#${activeSwatchGroupId} ~ .swatch-option-message`
    );

    $("[data-option-value]", $swatchGroup).text(swatchName);
    $swatchOptionMessage.text(
      `${this.swatchInitMessageStorage[activeSwatchGroupId]} ${swatchName}`
    );
    this.setLiveRegionAttributes($swatchOptionMessage, "status", "assertive");
  }

  setLiveRegionAttributes($element, roleType, ariaLiveStatus) {
    $element.attr({
      role: roleType,
      "aria-live": ariaLiveStatus,
    });
  }

  checkIsQuickViewChild($element) {
    return !!$element.parents(".quickView").length;
  }

  showProductImage(image) {
    if (isPlainObject(image)) {
      const zoomImageUrl = utils.tools.imageSrcset.getSrcset(
        image.data,
        { "1x": this.context.zoomSize }
        /*
                    Should match zoom size used for data-zoom-image in
                    components/products/product-view.html

                    Note that this will only be used as a fallback image for browsers that do not support srcset

                    Also note that getSrcset returns a simple src string when exactly one size is provided
                */
      );

      const mainImageUrl = utils.tools.imageSrcset.getSrcset(
        image.data,
        { "1x": this.context.productSize }
        /*
                    Should match fallback image size used for the main product image in
                    components/products/product-view.html

                    Note that this will only be used as a fallback image for browsers that do not support srcset

                    Also note that getSrcset returns a simple src string when exactly one size is provided
                */
      );

      const mainImageSrcset = utils.tools.imageSrcset.getSrcset(image.data);

      this.imageGallery.setAlternateImage({
        mainImageUrl,
        zoomImageUrl,
        mainImageSrcset,
      });
    } else {
      this.imageGallery.restoreImage();
    }
  }

  /**
   *
   * Handle action when the shopper clicks on + / - for quantity
   *
   */
  listenQuantityChange() {
    this.$scope.on("click", "[data-quantity-change] button", (event) => {
      event.preventDefault();
      const $target = $(event.currentTarget);
      const viewModel = this.getViewModel(this.$scope);
      const $input = viewModel.quantity.$input;
      const quantityMin = parseInt($input.data("quantityMin"), 10);
      const quantityMax = parseInt($input.data("quantityMax"), 10);

      let qty = forms.numbersOnly($input.val())
        ? parseInt($input.val(), 10)
        : quantityMin;
      // If action is incrementing
      if ($target.data("action") === "inc") {
        qty = forms.validateIncreaseAgainstMaxBoundary(qty, quantityMax);
      } else if (qty > 1) {
        qty = forms.validateDecreaseAgainstMinBoundary(qty, quantityMin);
      }

      // update hidden input
      viewModel.quantity.$input.val(qty);
      // update text
      viewModel.quantity.$text.text(qty);
      // perform validation after updating product quantity
      this.addToCartValidator.performCheck();

      this.updateProductDetailsData();
    });

    // Prevent triggering quantity change when pressing enter
    this.$scope.on("keypress", ".form-input--incrementTotal", (event) => {
      // If the browser supports event.which, then use event.which, otherwise use event.keyCode
      const x = event.which || event.keyCode;
      if (x === 13) {
        // Prevent default
        event.preventDefault();
      }
    });

    this.$scope.on("keyup", ".form-input--incrementTotal", () => {
      this.updateProductDetailsData();
    });
  }
  /**
   * Get open value field options which is a Modifier Option(Number Only Text field)
   * we configured in BC.
   * @returns {Object|null} The options for open value field or null if not found.
   */
  getOpenValueFieldOptions() {
    return currentProductDetails.options.find(
      (option) =>
        option.type === "Configurable_Entry_NumbersOnlyText" &&
        option.limit_input_option === "range" &&
        option.display_name === INPUT_OPEN_VALUE_PRODUCT_MODIFIER_NAME
    );
  }
  /**
   * Get fixed value field options which is Variant Option configured we configured in BC
   * @returns {Object|null} The options for open value field or null if not found.
   */
  getFixedValuesFieldOptions() {
    return currentProductDetails.options.find(
      (option) =>
        option.type === "Configurable_PickList_Set" &&
        option.state === "variant_option"
    );
  }
  /**
   *
   * Add a product to cart
   *
   */
  async addProductToCart(event, form) {
    const $addToCartBtn = $("#form-action-addToCart", $(event.target));
    const $addToCartBtnAndCheckoutBtn = $(
      "#form-action-addToCartAndCheckout",
      $(event.target)
    );
    const originalBtnVal = $addToCartBtn.val();
    const waitMessage = $addToCartBtn.data("waitMessage");

    // Do not do AJAX if browser doesn't support FormData
    if (window.FormData === undefined) {
      return;
    }

    // Prevent default
    event.preventDefault();

    $addToCartBtn.val(waitMessage).prop("disabled", true);
    $addToCartBtnAndCheckoutBtn.prop("disabled", true);

    this.$overlay.show();

    const formData = normalizeFormData(new FormData(form));
    const productId = formData.get("product_id").toString();
    const existingCart = await getCart();
    if (existingCart) {
      const digitalItems = existingCart?.lineItems?.digitalItems || [];
      const digitalItemMatch = digitalItems.find(
        (item) => item.productId.toString() === productId
      );
      if (
        digitalItemMatch &&
        digitalItemMatch?.quantity >= ENV_CART_ITEMS_PER_ENTITY_LIMIT
      ) {
        showAlertModal(
          `${replaceTranslationPlaceholders(
            window.translations.cart_per_product_limit_error_message,
            { limit: ENV_CART_ITEMS_PER_ENTITY_LIMIT }
          )}
          <div>
            <a href="/cart.php" style="text-decoration:underline !important; margin-top:5px">${
              window.translations.go_to_cart_text
            }</a>.
          </div>
          `
        );
        $addToCartBtn.val(originalBtnVal).prop("disabled", false);
        $addToCartBtnAndCheckoutBtn.prop("disabled", false);
        return;
      }
      if (digitalItems && digitalItems?.length >= ENV_CART_ENTITIES_LIMIT) {
        showAlertModal(
          `${replaceTranslationPlaceholders(
            window.translations.cart_items_limit_error_message,
            { limit: ENV_CART_ENTITIES_LIMIT }
          )}
          <div>
            <a href="/cart.php" style="text-decoration:underline !important; margin-top:5px">${
              window.translations.go_to_cart_text
            }</a>.
          </div>
          `
        );
        $addToCartBtn.val(originalBtnVal).prop("disabled", false);
        $addToCartBtnAndCheckoutBtn.prop("disabled", false);
        return;
      }
    }
    let selectedFaceValue = null;
    let selectedVariantId = null;
    const openValueFieldOptions = this.getOpenValueFieldOptions();

    // For open value products, get field value from the Modifier Option form field
    if (openValueFieldOptions) {
      formData.forEach((value, key) => {
        if (key === `attribute[${openValueFieldOptions.id}]`) {
          selectedFaceValue = value;
        }
      });
    } else {
      // for fixed denom products, get face value and v3 variant id from product attributes storefront API
      const productOptionsResponse = await this.getProductAttributes();
      selectedFaceValue = productOptionsResponse.data.price.without_tax.value;
      selectedVariantId = productOptionsResponse.data.v3_variant_id;
    }
    let cartId = existingCart?.id;
    if (existingCart) {
      await this.addProductToCartViaApi({
        productId,
        faceValue: selectedFaceValue,
        variantId: selectedVariantId,
        cartId: cartId,
      });

      this.updateUIAfterSuccess(
        event,
        cartId,
        originalBtnVal,
        $addToCartBtn,
        $addToCartBtnAndCheckoutBtn
      );
    } else {
      utils.api.cart.itemAdd(formData, async (err, response) => {
        try {
          if (err) {
            throw new Error(err);
          }

          if (!response) {
            throw new Error("No response received from cart API");
          }

          if (response.data.error) {
            throw new Error(response.data.error);
          }
          const itemId = response.data?.cart_item?.id;
          cartId = response.data.cart_id;
          await this.addProductToCartViaApi({
            productId,
            faceValue: selectedFaceValue,
            variantId: selectedVariantId,
            cartId: cartId,
          });
        } catch (error) {
          console.error("Error in cart itemAdd callback:", error);
          if (window.Sentry) {
            Sentry.captureException(error, {
              tags: {
                product_id: productId,
                face_value: selectedFaceValue,
                variant_id: selectedVariantId,
              },
            });
          }
          $addToCartBtn.val(originalBtnVal).prop("disabled", false);
          $addToCartBtnAndCheckoutBtn.prop("disabled", false);
          this.$overlay.hide();

          const errorMessage =
            error.message || window.translations.add_to_cart_error_message;

          this.handleError(new Error(errorMessage), $addToCartBtn);
        } finally {
          // Ensure invalid items are removed from cart by retrying a few times
          // if something goes wrong due to intermittent issue with any API call
          await retry(removeInvalidItemsFromCart, 2, 1000);

          this.updateUIAfterSuccess(
            event,
            cartId,
            originalBtnVal,
            $addToCartBtn,
            $addToCartBtnAndCheckoutBtn
          );
        }
      });
    }

    this.setLiveRegionAttributes($addToCartBtn.next(), "status", "polite");
  }

  /**
   * Uses add to cart API to add product to cart
   * @param {Object} param0: Accepts productId, faceValue, variantId and cartId
   */
  async addProductToCartViaApi({ productId, faceValue, variantId, cartId }) {
    await addProductToCartViaApiService(
      productId,
      cartId,
      faceValue,
      variantId
    );
  }

  /**
   * Update UI elements after successful cart operation, opening cart preview modal
   * Or redirecting to checkout if checkout button was clicked
   */
  updateUIAfterSuccess(
    addToCartEvent,
    cartId,
    originalBtnVal,
    $addToCartBtn,
    $addToCartBtnAndCheckoutBtn
  ) {
    if (
      addToCartEvent.originalEvent.submitter.name === "add-to-cart-and-checkout"
    ) {
      window.location.href = "/checkout";
      return;
    }
    try {
      const cartDropdown = document.querySelector(
        'a[data-dropdown="cart-preview-dropdown"]'
      );
      if (cartDropdown) {
        cartDropdown.click();
      }

      $addToCartBtn.val(originalBtnVal).prop("disabled", false);
      $addToCartBtnAndCheckoutBtn.prop("disabled", false);

      if (cartId) {
        currencySelector(cartId);
      }

      this.$overlay.hide();

      if (cartId) {
        this.updateCartContent(this.previewModal, cartId);
      }
    } catch (error) {
      console.error("Error updating UI after success:", error);
      // Fallback: just hide overlay and re-enable buttons
      this.$overlay.hide();
      $addToCartBtn.val(originalBtnVal).prop("disabled", false);
      $addToCartBtnAndCheckoutBtn.prop("disabled", false);
    }
  }

  /**
   * Handle errors during cart operations
   */
  handleError(error, $addToCartBtn) {
    let message = window.translations.add_to_cart_error_message;

    if (error instanceof InvalidInputFaceValueError) {
      message = `${message}\n${error.message}`;
    }

    if (!this.checkIsQuickViewChild($addToCartBtn)) {
      alertModal().$preModalFocusedEl = $addToCartBtn;
    }

    showAlertModal(message);
  }
  /**
   * Get cart contents
   *
   * @param {String} cartItemId
   * @param {Function} onComplete
   */
  getCartContent(cartItemId, onComplete) {
    const options = {
      template: "cart/preview",
      params: {
        suggest: cartItemId,
      },
      config: {
        cart: {
          suggestions: {
            limit: 4,
          },
        },
      },
    };

    utils.api.cart.getContent(options, onComplete);
  }

  /**
   * Redirect to url
   *
   * @param {String} url
   */
  redirectTo(url) {
    if (this.isRunningInIframe() && !window.iframeSdk) {
      window.top.location = url;
    } else {
      window.location = url;
    }
  }

  setRedemptionInformation() {
    // set country redeemable in
    let countryRedeemableIn = currentProductDetails.custom_fields.find(
      (field) => field.name === "countries_redeemable_in"
    )?.value;
    if (countryRedeemableIn) {
      countryRedeemableIn = JSON.parse(countryRedeemableIn);
    }
    $("[data-redemption-location]").text(
      getCountryNameFromCode(countryRedeemableIn, window.channelLocale)
    );

    // set expiry information
    let expiryData = currentProductDetails.custom_fields.find(
      (field) => field.name === "expiry"
    )?.value;

    if (expiryData) {
      expiryData = JSON.parse(expiryData);
      // expiry data is nested json
      if (typeof expiryData === "string") {
        expiryData = JSON.parse(expiryData);
      }
    }
    $("[data-expiry-date]").text(
      getProductExpiryDisplayText(expiryData["in_months"], expiryData["type"])
    );

    // set redeemable at
    let redeemableAt = currentProductDetails.custom_fields.find(
      (field) => field.name === "redeemable_at"
    )?.value;
    if (redeemableAt) {
      redeemableAt = JSON.parse(redeemableAt);
    }
    $("[data-redeemable-at]").text(
      getProductRedeemableAtDisplayText(redeemableAt)
    );
  }

  /**
   * Update cart content
   *
   * @param {Modal} modal
   * @param {String} cartItemId
   * @param {Function} onComplete
   */
  updateCartContent(modal, cartItemId, onComplete) {
    this.getCartContent(cartItemId, (err, response) => {
      if (err) {
        return;
      }

      modal.updateContent(response);

      // Update cart counter
      const $body = $("body");
      const $cartQuantity = $("[data-cart-quantity]", modal.$content);
      const $cartCounter = $(".navUser-action .cart-count");
      const quantity = $cartQuantity.data("cartQuantity") || 0;
      const $promotionBanner = $("[data-promotion-banner]");
      const $backToShopppingBtn = $(
        ".previewCartCheckout > [data-reveal-close]"
      );
      const $modalCloseBtn = $("#previewModal > .modal-close");
      const bannerUpdateHandler = () => {
        const $productContainer = $("#main-content > .container");

        $productContainer.append(
          '<div class="loadingOverlay pdp-update"></div>'
        );
        $(".loadingOverlay.pdp-update", $productContainer).show();
        window.location.reload();
      };

      $cartCounter.addClass("cart-count--positive");
      $body.trigger("cart-quantity-update", quantity);

      if (onComplete) {
        onComplete(response);
      }

      if ($promotionBanner.length && $backToShopppingBtn.length) {
        $backToShopppingBtn.on("click", bannerUpdateHandler);
        $modalCloseBtn.on("click", bannerUpdateHandler);
      }
    });
  }

  /**
   * Hide or mark as unavailable out of stock attributes if enabled
   * @param  {Object} data Product attribute data
   */
  updateProductAttributes(data) {
    super.updateProductAttributes(data);
    this.showProductImage(data.image);
  }

  updateProductDetailsData() {
    const $form = $("form[data-cart-item-add]");
    const formDataItems = $form.serializeArray();

    const productDetails = {};

    for (const formDataItem of formDataItems) {
      const { name, value } = formDataItem;

      if (name === "product_id") {
        productDetails.productId = Number(value);
      }

      if (name === "qty[]") {
        productDetails.quantity = Number(value);
      }

      if (name.match(/attribute/)) {
        const productOption = {
          optionId: Number(name.match(/\d+/g)[0]),
          optionValue: value,
        };

        productDetails.optionSelections = productDetails?.optionSelections
          ? [...productDetails.optionSelections, productOption]
          : [productOption];
      }
    }

    document.dispatchEvent(
      new CustomEvent("onProductUpdate", {
        bubbles: true,
        detail: { productDetails },
      })
    );
  }
}
